import React, { useMemo, useCallback, useState } from 'react';
import {
    IconDots,
    IconArrowsMoveVertical,
    IconUserX,
    IconPencil,
    IconMailForward,
    IconUserPlus,
} from '@tabler/icons-react';
import { Divider, Flex, Highlight, Text, Button, Menu } from '@mantine/core';
import { useMutation } from '@apollo/client';
import { TableActions } from '../common';

import { useOrganisationSearch } from '../../context/OrganisationSearchContext';
import { HierarchyUser } from '../../codegen/graphql';
import stringifyNumber from '../../utility/stringifyNumber';
import { useAuth } from '../../context/AuthProvider';
import { checkUserRole } from '../../utility/userRoles';
import { UserRole } from '../../types/users';
import { SEND_INVITE } from '../../apollo/mutations/auth';
import {
    showLoadingNotification,
    updateFailedNotification,
    updateSuccessNotification,
} from '../../notifications';
import { useUserPortal } from '../../context/UserPortalContext';
import { UPDATE_USER } from '../../apollo/mutations/user';
import { GET_USERS } from '../../apollo/queries/user';
import { GET_OPS_HIERARCHY } from '../../apollo/queries/opsHierarchy';
import { GET_STORES } from '../../apollo/queries/store';

type StoreManagerDetailsItemProps = {
    readonly manager: HierarchyUser;
    readonly showDesignation?: boolean;
    readonly divider?: boolean;
    readonly moveManager: () => void;
    readonly assignManager: () => void;
    readonly canAssignManager?: boolean;
    readonly nestedInPopover?: boolean;
    readonly onCloseManagersPopover?: () => void;
};

function StoreManagerDetailsItem({
    manager,
    showDesignation = true,
    divider = false,
    moveManager,
    assignManager,
    canAssignManager = false,
    nestedInPopover = false,
    onCloseManagersPopover,
}: StoreManagerDetailsItemProps) {
    const { organisationSearch } = useOrganisationSearch();
    const { authUser } = useAuth();
    const { openUserSidesheet } = useUserPortal();
    const [actionsMenuOpen, setActionsMenuOpen] = useState(false);

    const [sendInvite] = useMutation(SEND_INVITE);

    const [updateUser] = useMutation(UPDATE_USER, {
        refetchQueries: [GET_USERS, GET_OPS_HIERARCHY, GET_STORES],
        awaitRefetchQueries: true,
    });

    const removeManager = useCallback(
        async (userId: string) => {
            try {
                showLoadingNotification(
                    'remove-manager',
                    'Removing manager',
                    'Please wait while we remove the manager',
                );
                await updateUser({
                    variables: {
                        input: {
                            id: userId,
                            managerStoreId: null,
                        },
                    },
                });
                updateSuccessNotification(
                    'remove-manager',
                    'Manager removed',
                    'Manager removed successfully',
                );
            } catch (error: any) {
                console.error('Failed to remove manager:', error);
                updateFailedNotification(
                    'remove-manager',
                    'Failed to remove manager',
                    'An error occured while trying to remove the manager.',
                );
            }
        },
        [updateUser],
    );

    const managerNumber = useMemo(() => {
        const number = manager?.mobileNo || manager?.telNo;

        if (!number) {
            return null;
        }

        return stringifyNumber(number);
    }, [manager]);

    const sendPortalInvite = useCallback(
        async (userId: string) => {
            try {
                showLoadingNotification(
                    'send-portal-invite',
                    'Sending portal invite',
                    'Please wait while we send the portal invite',
                );
                await sendInvite({
                    variables: {
                        userId,
                    },
                });
                updateSuccessNotification(
                    'send-portal-invite',
                    'Portal invite sent',
                    'Portal invite sent successfully',
                );
            } catch (error: any) {
                console.error('Failed to send portal invite:', error);
                updateFailedNotification(
                    'send-portal-invite',
                    'Failed to send portal invite',
                    'An error occured while trying to send the portal invite. Please wait one minute and try again.',
                );
            }
        },
        [sendInvite],
    );

    const openEditUserSidesheet = useCallback(() => {
        openUserSidesheet(manager);
    }, [manager, openUserSidesheet]);

    const options = useMemo((): Array<{
        label: string;
        icon: React.ReactNode;
        onClick: () => void;
        isShown?: boolean;
    }> => {
        const isNotAuthUser = authUser?.id !== manager.id;
        const isDivisionalManager = checkUserRole(authUser, [
            UserRole.DIVISIONAL_MANAGER,
        ]);
        const isAdminOrSuperAdmin = checkUserRole(authUser, [
            UserRole.ADMIN,
            UserRole.SUPER_ADMIN,
        ]);

        const baseOptions = [
            {
                label: 'Edit Manager Details',
                icon: <IconPencil className="text-primary" size={20} />,
                onClick: openEditUserSidesheet,
                isShown: true,
            },
            {
                label: 'Move Manager',
                icon: <IconArrowsMoveVertical size={20} color="purple" />,
                onClick: moveManager,
                isShown: true,
            },
            {
                label: 'Resend Invite',
                icon: <IconMailForward size={20} className="text-blue-500" />,
                onClick: () => manager.id && sendPortalInvite(manager.id),
                isShown: true,
            },
        ];

        if (canAssignManager || isAdminOrSuperAdmin) {
            baseOptions.push({
                label: 'Assign Manager',
                icon: <IconUserPlus size={20} className="text-green-500" />,
                onClick: assignManager,
                isShown: true,
            });
        }

        if (isDivisionalManager) {
            return [
                ...baseOptions,
                {
                    label: 'Remove from Store',
                    icon: <IconUserX size={20} className="text-orange-400" />,
                    onClick: () => removeManager(manager.id ?? ''),
                    isShown: true,
                },
            ];
        }

        if (isAdminOrSuperAdmin) {
            return [
                ...baseOptions,
                {
                    label: 'Remove Manager',
                    icon: <IconUserX size={20} className="text-orange-400" />,
                    onClick: () => removeManager(manager.id ?? ''),
                    isShown: isNotAuthUser,
                },
            ];
        }

        return [];
    }, [
        authUser,
        manager,
        moveManager,
        removeManager,
        assignManager,
        sendPortalInvite,
        openEditUserSidesheet,
        canAssignManager,
    ]);

    const visibleOptions = useMemo(
        () => options.filter(option => option.isShown),
        [options],
    );

    const runNestedAction = useCallback(
        (action: () => void) => {
            action();
            setActionsMenuOpen(false);
            onCloseManagersPopover?.();
        },
        [onCloseManagersPopover],
    );

    const nestedActionsTrigger = (
        <Button
            variant="subtle"
            className="text-primary rounded-md p-1 w-min h-min"
            onMouseDown={event => event.stopPropagation()}
        >
            <IconDots />
        </Button>
    );

    const actionsTrigger = (
        <Button
            variant="subtle"
            className="text-primary rounded-md p-1 w-min h-min"
        >
            <IconDots />
        </Button>
    );

    return (
        <>
            <div className="flex flex-1 w-full justify-between items-center">
                <div className="flex gap-1">
                    <div className="flex flex-col">
                        <Text className="whitespace-normal text-ellipsis overflow-hidden text-xs mt-1">
                            <Highlight
                                className="text-sm"
                                highlight={organisationSearch}
                            >{`${manager.firstName} ${manager.lastName}`}</Highlight>
                        </Text>
                        {showDesignation && (
                            <Text className="text-xs" color="dimmed">
                                <Highlight
                                    className="text-xs"
                                    highlight={organisationSearch}
                                >
                                    {manager.designation ?? ''}
                                </Highlight>
                            </Text>
                        )}
                        {managerNumber && (
                            <Flex gap="2px">
                                <Text className="text-sm">Tel:</Text>
                                <a
                                    href={`tel:${managerNumber}`}
                                    type="tel"
                                    className="hover:underline text-xs text-primary"
                                >
                                    <Highlight
                                        className="text-sm"
                                        highlight={organisationSearch}
                                    >
                                        {managerNumber}
                                    </Highlight>
                                </a>
                            </Flex>
                        )}
                    </div>
                </div>
                {nestedInPopover ? (
                    <Menu
                        opened={actionsMenuOpen}
                        onChange={setActionsMenuOpen}
                        withinPortal
                        zIndex={1000}
                        width={200}
                        shadow="md"
                        position="bottom-end"
                    >
                        <Menu.Target>{nestedActionsTrigger}</Menu.Target>
                        <Menu.Dropdown>
                            {visibleOptions.length === 0 ? (
                                <Menu.Item
                                    disabled
                                    className="capitalize justify-center text-center"
                                >
                                    No options available
                                </Menu.Item>
                            ) : (
                                visibleOptions.map(option => (
                                    <Menu.Item
                                        key={option.label}
                                        leftSection={option.icon}
                                        onClick={() =>
                                            runNestedAction(option.onClick)
                                        }
                                        className="capitalize"
                                    >
                                        {option.label}
                                    </Menu.Item>
                                ))
                            )}
                        </Menu.Dropdown>
                    </Menu>
                ) : (
                    <TableActions
                        row={manager}
                        options={options.map(option => ({
                            ...option,
                            onClick: () => option.onClick(),
                        }))}
                    >
                        {actionsTrigger}
                    </TableActions>
                )}
            </div>

            {divider && <Divider my="10px" />}
        </>
    );
}

export default StoreManagerDetailsItem;
